/**
 * Krok 5: Podsumowanie — publikacja (Kreator Hodowli).
 * Buduje checklistę pól hodowli i renderuje wspólny WizardSummaryStep.
 */
import WizardSummaryStep from '@/components/wizard/WizardSummaryStep';
import FontStylePicker from '@/components/wizard/FontStylePicker';
import LivePagePreview from '@/components/wizard/LivePagePreview';
import ThemePicker from '@/components/wizard/ThemePicker';

const DESC_MIN = 120;

export default function Step7Done({
  data, updateData, termsAccepted, setTermsAccepted,
  goToStep, onPublish, onSaveLater, submitting, isEdit,
}) {
  const name = (data.name || '').trim();
  const breeds = data.breeds || [];
  const city = (data.city || '').trim();
  const location = [data.city, data.voivodeship].filter(Boolean).join(', ');
  const cover = data.cover_image || data.logo;
  const desc = (data.description || '').trim();
  const gallery = data.gallery || [];
  const contact = [data.phone, data.whatsapp, data.email].filter(Boolean);

  const checklist = [
    { icon: 'building', label: 'Nazwa hodowli', ok: !!name, value: name || 'Uzupełnij nazwę', hint: 'Pełna nazwa hodowli (najlepiej z przydomkiem FCI).', step: 1, required: true },
    { icon: 'paw', label: 'Rasa', ok: breeds.length > 0, value: breeds.length ? breeds.join(', ') : 'Wybierz rasę', hint: 'Wybierz rasy, które hodujesz — po nich klienci Cię znajdą.', step: 1, required: true },
    { icon: 'map', label: 'Lokalizacja', ok: !!city, value: location || 'Uzupełnij lokalizację', hint: 'Miasto i województwo — klienci szukają hodowli w pobliżu.', step: 1, required: true },
    { icon: 'photo', label: 'Zdjęcie główne', ok: !!cover, value: cover ? 'Dodane' : 'Brak zdjęcia', hint: 'Profile ze zdjęciem dostają znacznie więcej zapytań.', step: 3, addLabel: 'Dodaj' },
    { icon: 'text', label: 'Opis hodowli', ok: desc.length >= DESC_MIN, value: desc ? (desc.length < DESC_MIN ? `Krótki opis (${desc.length} znaków)` : 'Uzupełniony') : 'Brak opisu', hint: 'Opisz swoje podejście do hodowli — to buduje zaufanie.', step: 2, addLabel: 'Uzupełnij' },
    { icon: 'gallery', label: 'Galeria zdjęć', ok: gallery.length > 0, value: gallery.length ? `${gallery.length} zdjęć` : 'Brak zdjęć', hint: 'Dodaj 3–6 zdjęć psów i otoczenia hodowli.', step: 3, addLabel: 'Dodaj' },
    { icon: 'phone', label: 'Dane kontaktowe', ok: contact.length > 0, value: contact.length ? contact.slice(0, 2).join(' · ') : 'Dodaj telefon lub e-mail', hint: 'Bez kontaktu klienci nie wyślą Ci zapytania.', step: 4 },
  ];

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
        <p className="text-sm font-semibold text-gray-900">Dostosuj wygląd swojej strony</p>
        <p className="text-xs text-gray-600 mt-0.5">Masz do wyboru: <span className="font-semibold">styl pisma</span> i <span className="font-semibold">motyw kolorystyczny</span>. Podgląd po prawej zmienia się na żywo.</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <FontStylePicker value={data.font_style} onChange={(v) => updateData({ font_style: v })} />
          <ThemePicker
            value={data.theme}
            onChange={(v) => updateData({ theme: v })}
            title="Motyw kolorystyczny"
            desc="Tło i charakter całej Twojej strony."
            options={[
              { id: 'dark', label: 'Ciemny', swatch: '#0A0E1A' },
              { id: 'light', label: 'Jasny', swatch: '#FDFBF7' },
              { id: 'green', label: 'Zielony', swatch: '#0A1F13' },
              { id: 'blue', label: 'Niebieski', swatch: '#0B1221' },
            ]}
          />
        </div>
        <div className="lg:sticky lg:top-4 self-start">
          <LivePagePreview
            fontStyle={data.font_style}
            eyebrow={(data.breeds && data.breeds[0]) || 'Hodowla'}
            title={name || 'Twoja hodowla'}
            subtitle={location}
            image={cover || (gallery && gallery[0]) || ''}
            theme={data.theme || 'dark'}
          />
        </div>
      </div>
      <WizardSummaryStep
      headerTitle="Twoja strona hodowli jest gotowa do publikacji"
      headerSubtitle="Po publikacji Twoja hodowla pojawi się w katalogu APISAI i otrzymasz własną stronę z formularzem kontaktowym."
      pageCardTitle="Strona hodowli"
      checklist={checklist}
      goToStep={goToStep}
      catalogChecked={data.show_in_catalog}
      onCatalogChange={(v) => updateData({ show_in_catalog: v })}
      catalogLabel="Chcę, aby moja hodowla była widoczna w katalogu APISAI."
      catalogTestId="wizard-v2-catalog-toggle"
      termsAccepted={termsAccepted}
      setTermsAccepted={setTermsAccepted}
      termsTestId="wizard-v2-terms-checkbox"
      dataAccurateTestId="wizard-v2-data-accurate"
      onPublish={onPublish}
      onSaveLater={onSaveLater}
      submitting={submitting}
      isEdit={isEdit}
      publishLabel="Opublikuj stronę hodowli"
      testId="wizard-v2-step-done"
      />
    </div>
  );
}
